import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SymbolsTabs } from './SymbolsTabs';
import { ShareBar } from './ShareBar';

const bnNum = (n, lang) => (lang === 'bn' ? String(n).replace(/\d/g, (d) => '০১২৩৪৫৬৭৮৯'[d]) : n);

const Figure = ({ k, image, alt, hotspots, active, setHover, onTap, maxW }) => {
  const { t, language } = useLanguage();
  return (
    <figure className={`mx-auto w-full rounded-xl bg-white p-6 shadow-xl ring-1 ring-gray-200 ${maxW}`} data-testid={`${k}-figure`}>
      <div className="relative">
        <img src={image.src} alt={t(alt)} width={image.w} height={image.h} className="block h-auto w-full" data-testid={`${k}-image`} />
        {hotspots.map((h) => {
          const on = active === h.card;
          return (
            <button key={h.n} type="button" aria-label={`${bnNum(h.n, language)}: ${t(h.label)}`} aria-pressed={on}
              onMouseEnter={() => setHover(h.card)} onMouseLeave={() => setHover(null)} onClick={() => onTap(h.card)}
              style={{ left: `${h.x}%`, top: `${h.y}%` }} data-testid={`${k}-hotspot-${h.n}`}
              className={`absolute flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-sm font-bold shadow-lg ring-2 ring-white transition-transform duration-200 ${on ? 'scale-125 bg-party-red text-white' : 'bg-white text-party-green hover:scale-110'}`}>
              <span className={`absolute inset-0 rounded-full ${on ? '' : 'animate-ping bg-white/60'}`} aria-hidden="true" />
              <span className="relative">{bnNum(h.n, language)}</span>
            </button>
          );
        })}
      </div>
      <figcaption className="mt-4 text-center text-xs text-gray-500">{t({ en: 'Hover or tap a number to read its meaning', bn: 'অর্থ জানতে কোনো নম্বরের উপর রাখুন বা ট্যাপ করুন' })}</figcaption>
    </figure>
  );
};

export const SymbolPage = ({ k, page, symbol, title, intro, image, alt, hotspots, cards, together, extra = null, maxW = 'max-w-[340px] sm:max-w-[380px]' }) => {
  const { t, language } = useLanguage();
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const active = hover || pinned;
  const onTap = (id) => {
    setPinned(id);
    const el = document.querySelector(`[data-testid="${k}-meaning-${id}"]`);
    if (el && window.matchMedia('(max-width: 1023px)').matches) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const spotOf = (id) => hotspots.find((h) => h.card === id)?.n;
  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid={`${page}-page`}>
      <section className="mx-auto max-w-6xl px-4 pb-10 pt-10 sm:px-6 lg:px-8">
        <SymbolsTabs />
        <div className="mt-8 grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <p className={`text-sm font-semibold uppercase text-party-green ${language === 'bn' ? '' : 'tracking-[0.2em]'}`}>{t({ en: 'National Democratic Party', bn: 'ন্যাশনাল ডেমোক্রেটিক পার্টি' })}</p>
            <h1 className="mt-3 text-4xl font-bold leading-tight text-gray-900 sm:text-5xl lg:text-6xl" data-testid={`${page}-title`}>{t(title)}</h1>
            <p className="mt-5 text-base text-gray-700 md:text-lg" data-testid={`${page}-intro`}>{t(intro)}</p>
            <ShareBar symbol={symbol} />
          </div>
          <Figure k={k} image={image} alt={alt} hotspots={hotspots} active={active} setHover={setHover} onTap={onTap} maxW={maxW} />
        </div>
      </section>
      {extra}
      <section className="mx-auto max-w-6xl px-4 pb-14 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2">
          {cards.map((c) => (
            <article key={c.id} data-testid={`${k}-meaning-${c.id}`} data-active={active === c.id}
              className={`rounded-xl border bg-white p-6 transition-shadow duration-300 ${active === c.id ? 'border-party-green shadow-lg ring-1 ring-party-green' : 'border-gray-200'}`}>
              <div className="mb-3 flex items-center gap-3">
                <img src={image.src} alt="" aria-hidden="true" className="h-6 w-auto shrink-0" />
                <h2 className="text-base font-bold text-gray-900 md:text-lg">
                  {spotOf(c.id) && <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-party-green align-middle text-xs text-white">{bnNum(spotOf(c.id), language)}</span>}
                  {t(c.title)}
                </h2>
              </div>
              <p className="text-sm leading-relaxed text-gray-700 md:text-base">{t(c.text)}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="bg-gray-900 py-14 text-white" data-testid={`${k}-together`}>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className={`text-base font-semibold uppercase text-party-green md:text-lg ${language === 'bn' ? '' : 'tracking-[0.2em]'}`}>{t({ en: 'Together', bn: 'সব মিলিয়ে' })}</h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-gray-100 md:text-xl" data-testid={`${k}-together-text`}>{t(together)}</p>
          <p className="mt-6 text-2xl font-bold text-party-red" data-testid={`${k}-zindabad`}>{t({ en: 'Bangladesh Zindabad.', bn: 'বাংলাদেশ জিন্দাবাদ।' })}</p>
          <ShareBar symbol={symbol} dark />
        </div>
      </section>
    </div>
  );
};
